import { useState } from 'react';
import { MessageSquareQuote } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Callout } from '../../components/ui/Callout';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { extractErrorMessage } from '../../api/errors';
import { useActivatePrompt, useBrandPrompts, useSavePrompt, type PromptVersion } from '../../queries/brandVoice';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
const KEY_PATTERN = /^[a-z][a-z0-9_]{0,49}$/;
// Must match get_brand_prompt() (changeset 0058): persona, separator, format.
const SEPARATOR = '\n\n⸻\n\n';
const textareaClass = 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed';

export function promptLabel(key: string): string {
  if (key === 'persona') return 'Persona';
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Parses the tokens editor; returns the object or an error message. */
export function parseTokens(text: string): { tokens?: Record<string, unknown>; error?: string } {
  try {
    const value: unknown = JSON.parse(text.trim() === '' ? '{}' : text);
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return { error: 'Tokens must be a JSON object, e.g. {"name": "Tippa"}' };
    return { tokens: value as Record<string, unknown> };
  } catch {
    return { error: 'Tokens are not valid JSON' };
  }
}

interface Draft {
  body: string;
  tokens: string;
  description: string;
}

interface VoicePanelProps {
  canWrite: boolean;
  brandName: string;
}

/**
 * The brand's persona (its voice, shared by every use) and one format per use
 * (the task and output rules for that email or post). Workflows get persona +
 * format as one system prompt. Every save is a new version and goes live
 * immediately; any older version can be restored.
 */
export function VoicePanel({ canWrite, brandName }: VoicePanelProps) {
  const { data, isLoading, error } = useBrandPrompts();
  const save = useSavePrompt();
  const activate = useActivatePrompt();
  const toast = useToast();
  const [selected, setSelected] = useState('persona');
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [addedKeys, setAddedKeys] = useState<string[]>([]);
  const [newKey, setNewKey] = useState('');
  const [restoring, setRestoring] = useState<PromptVersion | null>(null);

  if (isLoading) return <Spinner />;
  if (error || !data) return <Callout variant="error" title="Could not load the brand voice">{extractErrorMessage(error, '')}</Callout>;

  const groups = data.prompts;
  const keys = [...groups.map((g) => g.promptKey), ...addedKeys.filter((k) => !groups.some((g) => g.promptKey === k))];
  if (!keys.includes('persona')) keys.unshift('persona');
  const group = groups.find((g) => g.promptKey === selected);
  const active = group?.versions.find((v) => v.is_active);
  const original: Draft = {
    body: active?.body ?? '',
    tokens: JSON.stringify(active?.tokens ?? {}, null, 2),
    description: group?.description ?? '',
  };
  const draft = drafts[selected] ?? original;
  const parsed = parseTokens(draft.tokens);
  const dirty =
    draft.body !== original.body ||
    draft.description !== original.description ||
    JSON.stringify(parsed.tokens ?? null) !== JSON.stringify(active?.tokens ?? {});
  const canSave = canWrite && dirty && draft.body.trim() !== '' && !parsed.error && !save.isPending;
  const nextVersion = (group?.versions[0]?.version ?? 0) + 1;
  const persona = groups.find((g) => g.promptKey === 'persona')?.versions.find((v) => v.is_active)?.body;

  const edit = (patch: Partial<Draft>) => setDrafts((prev) => ({ ...prev, [selected]: { ...draft, ...patch } }));

  const submit = () =>
    save.mutate(
      { promptKey: selected, body: draft.body, tokens: parsed.tokens ?? {}, description: draft.description.trim() || null },
      {
        onSuccess: ({ prompt }) => {
          setDrafts(({ [selected]: _saved, ...rest }) => rest);
          toast.success(`${promptLabel(selected)} saved as version ${prompt.version}. Workflows use it from their next run.`);
        },
        onError: (err) => toast.error(extractErrorMessage(err, `Failed to save ${promptLabel(selected)}.`)),
      },
    );

  const addFormat = () => {
    const key = newKey.trim();
    if (!KEY_PATTERN.test(key)) {
      toast.error('Format keys are lower-case letters, digits and underscores, e.g. social_facebook.');
      return;
    }
    if (!keys.includes(key)) setAddedKeys((prev) => [...prev, key]);
    setSelected(key);
    setNewKey('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquareQuote className="h-4 w-4" /> Voice
        </CardTitle>
        <CardDescription>
          {brandName}'s persona is who is speaking, and it is shared by every email and social post. Each format holds the rules for one use.
          Workflows receive the persona followed by the format. A save goes live on the next workflow run, and older versions can be
          restored.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div role="tablist" aria-label="Persona and formats" className="flex flex-wrap gap-2">
          {keys.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={key === selected}
              onClick={() => setSelected(key)}
              className={`rounded-md border px-3 py-1.5 text-sm ${key === selected ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-accent'}`}
            >
              {promptLabel(key)}
              {drafts[key] && <span className="ml-1 text-amber-600" title="Unsaved changes">•</span>}
            </button>
          ))}
        </div>

        {!group && selected !== 'persona' && (
          <Callout variant="info" title={`New format: ${selected}`}>
            Nothing uses this key until a workflow asks for it by name. Saving creates version 1.
          </Callout>
        )}

        <div className="space-y-4">
          <label className="block text-sm">
            <span className="text-muted-foreground">What it is for</span>
            <input
              className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
              value={draft.description}
              disabled={!canWrite}
              onChange={(e) => edit({ description: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground">{selected === 'persona' ? 'Persona (voice, tone, character)' : 'Format instructions'}</span>
            <textarea
              className={textareaClass}
              rows={18}
              spellCheck
              value={draft.body}
              disabled={!canWrite}
              onChange={(e) => edit({ body: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground">Tokens (JSON: values the workflow fills in itself, e.g. subject prefix, labels, colours)</span>
            <textarea
              className={textareaClass}
              rows={6}
              spellCheck={false}
              value={draft.tokens}
              disabled={!canWrite}
              aria-invalid={Boolean(parsed.error)}
              onChange={(e) => edit({ tokens: e.target.value })}
            />
            {parsed.error && <span className="mt-1 block text-xs text-destructive">{parsed.error}</span>}
          </label>

          {canWrite && (
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={submit} disabled={!canSave}>
                {save.isPending ? 'Saving…' : dirty ? `Save as version ${nextVersion}` : 'No changes'}
              </Button>
              {drafts[selected] && (
                <Button variant="ghost" onClick={() => setDrafts(({ [selected]: _discarded, ...rest }) => rest)}>
                  Discard changes
                </Button>
              )}
            </div>
          )}

          {selected !== 'persona' && persona && (
            <details className="rounded-md border border-border p-3 text-sm">
              <summary className="cursor-pointer text-muted-foreground">Preview what the workflow receives</summary>
              <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap font-mono text-xs">{persona + SEPARATOR + draft.body}</pre>
            </details>
          )}
        </div>

        {group && (
          <div>
            <h4 className="mb-2 text-sm font-medium">Version history</h4>
            <ul className="divide-y divide-border rounded-md border border-border">
              {group.versions.map((v) => (
                <li key={v.version} className="px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">Version {v.version}</span>
                    {v.is_active && <Badge variant="success">Live</Badge>}
                    <span className="text-xs text-muted-foreground">
                      {DATE_FORMATTER.format(new Date(v.created_at))}
                      {v.created_by ? ` by ${v.created_by}` : ''}
                    </span>
                    {canWrite && !v.is_active && (
                      <Button size="sm" variant="outline" className="ml-auto" onClick={() => setRestoring(v)}>
                        Restore
                      </Button>
                    )}
                  </div>
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-muted-foreground">Show text</summary>
                    <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap font-mono text-xs">{v.body}</pre>
                  </details>
                </li>
              ))}
            </ul>
          </div>
        )}

        {canWrite && (
          <form
            className="flex flex-wrap items-end gap-2 border-t border-border pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              addFormat();
            }}
          >
            <label className="text-sm">
              <span className="text-muted-foreground">Add a format</span>
              <input
                className="mt-1 block w-64 rounded-md border border-input bg-background px-2 py-2 font-mono text-sm"
                placeholder="e.g. social_facebook"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
            </label>
            <Button type="submit" variant="outline" disabled={!newKey.trim()}>
              Add
            </Button>
          </form>
        )}
      </CardContent>

      <ConfirmDialog
        open={restoring !== null}
        title={restoring ? `Restore ${promptLabel(restoring.prompt_key)} version ${restoring.version}?` : ''}
        description={
          restoring
            ? `Version ${restoring.version} becomes live for ${brandName} and workflows use it from their next run. The current version stays in the history.`
            : ''
        }
        confirmLabel="Restore"
        busy={activate.isPending}
        onConfirm={() =>
          restoring &&
          activate.mutate(
            { promptKey: restoring.prompt_key, version: restoring.version },
            {
              onSuccess: () => {
                toast.success(`${promptLabel(restoring.prompt_key)} version ${restoring.version} is live.`);
                setDrafts(({ [restoring.prompt_key]: _stale, ...rest }) => rest);
                setRestoring(null);
              },
              onError: (err) => {
                toast.error(extractErrorMessage(err, 'Failed to restore.'));
                setRestoring(null);
              },
            },
          )
        }
        onOpenChange={(open) => !open && !activate.isPending && setRestoring(null)}
      />
    </Card>
  );
}
